/** Rebuildable public output. No authored/private content or database clients belong in this module. */
export const publishedSiteFormat = 1;
export const publishedSiteMarker = "X-Drate-Public-Snapshot";
export const publishedSiteBuildHeader = "X-Drate-Site-Build";
export const publicationLimits = {
  routes: 768,
  hosts: 9,
  representations: 4096,
  bodyBytes: 4 * 1024 * 1024,
  manifestBytes: 2 * 1024 * 1024,
  selectorBytes: 8192,
  selectorLifetimeMs: 90_000,
  // How long past expiry a public page may still use the last selector when the renewal cron is
  // down. Revocation writes a new selector, so only a job outage is bridged.
  selectorGraceMs: 10 * 60_000,
  selectorBatch: 32,
  selectorConcurrency: 4,
  batchMs: 10_000,
  batchTasks: 12,
  batchAssets: 16,
  verificationObjects: 32,
  verificationBytes: 8 * 1024 * 1024,
  attempts: 8,
  assets: 4096,
  assetBytes: 20 * 1024 * 1024,
} as const;

export type PublicationDependency =
  | "chrome"
  | "pages"
  | "catalog"
  | "content"
  | "bible"
  | "matchups"
  | "commissions";
export type PublicationHost = { origin: string; domainId: string | null };
export type PublicationRoute = {
  path: string;
  /** A session may open a fresh authorized body. Public presentation never contains that body. */
  policy: "public" | "viewer";
  dependencies: PublicationDependency[];
};
export type PublicationRepresentation = {
  /** Fingerprint of the published inputs, excluding delivery bookkeeping. */
  inputHash?: string;
  origin: string;
  /** Normalized path and query, including Start's exact build-specific serialized input. */
  request: string;
  route: string;
  policy: PublicationRoute["policy"];
  hash: string;
  bytes: number;
  status: number;
  headers: [string, string][];
  assets?: string[];
};
/** What a publication may store and serve beside its pages: pictures, and the uploaded fonts a
 * creator's look declares (Style › Fonts). Capture and every manifest reader share this list. */
export const publicationAssetType =
  /^(?:image\/(?:png|jpeg|webp|gif|avif|svg\+xml)|font\/(?:woff2|woff|ttf|otf))$/;

export type PublicationAsset = {
  source: string;
  hash: string;
  bytes: number;
  type: string;
  sourceVersion?: string;
};
export type PublicationManifest = {
  format: 1;
  reuseVersion?: 1;
  verification?: { version: 1; parent?: string };
  /** Prepared auth/query binding and independently authorized private-data enhancement. */
  presentation?: 2;
  enhancedPaths?: string[];
  id: string;
  creatorId: string;
  sourceVersion: number;
  safetyVersion: number;
  build: string;
  hosts: PublicationHost[];
  routes: PublicationRoute[];
  representations: PublicationRepresentation[];
  fallbacks: { path: string; reason: string }[];
  createdAt: number;
  assets?: PublicationAsset[];
  publicFunctions?: string[];
  /** Approved public feature/provider scope, checked by bounded reconciliation. */
  publicAccess?: string;
};
export type PublicationSelector = {
  format: 1;
  creatorId: string;
  /** Monotonic outbox version. Refreshes of the same version also advance issuedAt. */
  version: number;
  keyRevision: number;
  sourceVersion: number;
  safetyVersion: number;
  active: { id: string; build: string; safetyVersion: number } | null;
  available?: boolean;
  /** Fresh selector-side protection also covers a stale derived routing key. */
  gate?: boolean;
  hosts: PublicationHost[];
  issuedAt: number;
  expiresAt: number;
};

export const publicationStoredHeaders = new Set([
  "content-type",
  "content-security-policy",
  "referrer-policy",
  "x-content-type-options",
  "x-frame-options",
  "x-tss-serialized",
  "x-robots-tag",
  "location",
]);
const identifier = (value: unknown): value is string =>
  typeof value === "string" && /^[a-zA-Z0-9_-]{1,200}$/.test(value);

/**
 * Stable encoding of public feature/provider decisions, independent of query ordering. Released
 * features outside the experiences (`commissions`) join only when on, so a site without any keeps
 * the fingerprint it had before they existed.
 */
export async function publicationAccessFingerprint(
  kinds: string[],
  sources: string[],
  features: string[] = [],
) {
  const decisions = [[...new Set(kinds)].sort(), [...new Set(sources)].sort()];
  if (features.length) decisions.push([...new Set(features)].sort());
  return publicationHash(new TextEncoder().encode(JSON.stringify(decisions)));
}

/** Automatic enrollment is the default; explicit lists remain a bounded rollback override. */
export function publishedSiteCanaries(value: string | undefined) {
  if (value?.trim() === "*") return "*";
  const ids = [
    ...new Set(
      value
        ?.split(",")
        .map((id) => id.trim())
        .filter(Boolean) ?? [],
    ),
  ];
  if (ids.length > publicationLimits.selectorBatch || ids.some((id) => !identifier(id)))
    throw new Error(
      `Published site rollout requires at most ${publicationLimits.selectorBatch} explicit creator IDs or a standalone wildcard.`,
    );
  return ids.join(",");
}
const integer = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const dependencies = new Set([
  "chrome",
  "pages",
  "catalog",
  "content",
  "bible",
  "matchups",
  "commissions",
]);
const hashPattern = /^[a-f0-9]{64}$/;
// Lives (/streams) are per viewer and change while they run: always the live application.
const privatePath =
  /^\/(?:api|rpc|billing|app|_app|_start|_drate|_published|account|checkout|shop|cart|sign-in|sign-up|forgot-password|reset-password|join|booking|training|preview|search|streams)(?:\/|$)/;
// Commissions' list and each service's page (/commissions/<service>) are prepared. A client's own
// request (/commissions/requests/<id>?k=…) is theirs alone and always the live application.
const commissionsPath = /^\/commissions(?:\/|$)/;
const preparedCommissionsPath = /^\/commissions(?:\/(?!requests\/?$)[a-z0-9-]+)?\/?$/;

/** Commissions' list or one service's page, which a publication with Commissions prepares. */
export function isCommissionsPagePath(path: string) {
  return preparedCommissionsPath.test(path);
}
const dynamicPath = /^\/(?:matchups|tournaments|join|booking|training)(?:\/|$)/;
const tracking = /^(?:utm_[a-z_]+|fbclid|gclid|gbraid|wbraid|msclkid|mc_cid|mc_eid)$/i;

/** A community's authored public page never makes its member feed a public route. */
export function publishedRoutePolicy(
  path: string,
  variesByViewer: boolean,
): PublicationRoute["policy"] {
  return /^\/(?:community|training)\/?$/.test(path) || variesByViewer ? "viewer" : "public";
}

/** Explicit live boundaries; an unknown authored address is still owned by the publication. */
export function publishedPresentationPath(path: string) {
  if (["/sign-in", "/sign-up", "/forgot-password", "/reset-password"].includes(path)) return true;
  if (/^\/matchups(?:\/[a-z0-9-]+(?:\/(?:early-game|mid-game|late-game|setup))?)?$/.test(path))
    return true;
  if (
    [
      "/merch/cart",
      "/merch/checkout",
      "/checkout",
      "/account",
      "/join",
      "/booking",
      "/tournaments",
    ].includes(path)
  )
    return true;
  return (
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !privatePath.test(path) &&
    !dynamicPath.test(path) &&
    !path.startsWith("/_serverFn/") &&
    !path.includes(".") &&
    (!/^\/(?:community|coaching)(?:\/|$)/.test(path) ||
      /^\/(?:community|coaching)\/?$/.test(path)) &&
    (!/^\/merch(?:\/|$)/.test(path) ||
      /^\/merch(?:\/(?:products|collections)\/[a-z0-9-]+)?\/?$/.test(path)) &&
    (!commissionsPath.test(path) || preparedCommissionsPath.test(path))
  );
}

/** Caller identity, cookies and authorization never become inputs to a published response. */
export function publishedRequestKey(request: Request): string | null {
  if (request.method !== "GET" && request.method !== "HEAD") return null;
  const url = new URL(request.url);
  const data = url.pathname.startsWith("/_serverFn/");
  // Only the real owner document preview is a live boundary. Extra public-function
  // parameters remain under publication ownership and cannot opt into private route data.
  if (!data && url.searchParams.get("preview") === "1") return null;
  if (publishedAssetHash(url.pathname)) return url.search ? null : url.pathname;
  // Preserve the native renderer's image URL/srcset contract. Only an exact approved
  // manifest alias is reusable; unknown images still take the live authorized API.
  if (/^\/api\/(?:media|shop\/assets)\/[a-zA-Z0-9-]+$/.test(url.pathname)) {
    if (request.headers.has("Authorization")) return null;
    const query = [...url.searchParams];
    if (!query.length) return url.pathname;
    if (
      query.length === 1 &&
      query[0]![0] === "w" &&
      /^(480|960|1440|1920|2560)$/.test(query[0]![1])
    )
      return url.pathname + "?w=" + query[0]![1];
    return null;
  }
  if (
    data &&
    (request.method !== "GET" ||
      request.headers.get("Sec-Fetch-Site") !== "same-origin" ||
      request.headers.get("X-TSR-ServerFn") !== "true")
  )
    return null;
  if (!data && !publishedPresentationPath(url.pathname)) return null;
  if (
    !data &&
    ["/sign-in", "/sign-up", "/forgot-password", "/reset-password"].includes(url.pathname)
  )
    return url.pathname + (url.searchParams.get("server") === "1" ? "?server=1" : "");
  if (!data && ["/merch/cart", "/merch/checkout", "/checkout", "/account"].includes(url.pathname))
    return url.pathname + (url.searchParams.get("server") === "1" ? "?server=1" : "");
  const query = [...url.searchParams].filter(([key]) => !tracking.test(key));
  if (
    data &&
    (query.length > 1 || query.some(([key, value]) => key !== "payload" || value.length > 8192))
  )
    // Keep the known public function under publication ownership. Returning null here
    // would send a malformed public input to newer live source data during preparation.
    return url.pathname + "?__invalid_publication_input";
  query.sort(([a, x], [b, y]) => a.localeCompare(b) || x.localeCompare(y));
  const search = new URLSearchParams(query).toString();
  return url.pathname + (search ? `?${search}` : "");
}

function validHost(value: unknown): value is PublicationHost {
  if (
    !object(value) ||
    typeof value.origin !== "string" ||
    !(value.domainId === null || identifier(value.domainId))
  )
    return false;
  try {
    const url = new URL(value.origin);
    return (
      url.origin === value.origin &&
      !url.username &&
      !url.password &&
      (url.protocol === "https:" ||
        (url.protocol === "http:" &&
          /^(?:localhost|127\.0\.0\.1|[a-z0-9.-]+\.localhost)$/.test(url.hostname)))
    );
  } catch {
    return false;
  }
}

function hosts(value: unknown): value is PublicationHost[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.length <= publicationLimits.hosts &&
    value.every(validHost) &&
    new Set(value.map((host) => host.origin)).size === value.length
  );
}

function decode(text: string, maxBytes: number): unknown {
  if (new TextEncoder().encode(text).byteLength > maxBytes) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function parsePublicationSelector(text: string): PublicationSelector | null {
  const value = decode(text, publicationLimits.selectorBytes);
  if (
    !object(value) ||
    value.format !== 1 ||
    !identifier(value.creatorId) ||
    !integer(value.version) ||
    !integer(value.keyRevision) ||
    !integer(value.sourceVersion) ||
    !integer(value.safetyVersion) ||
    (value.available !== undefined && typeof value.available !== "boolean") ||
    (value.gate !== undefined && typeof value.gate !== "boolean") ||
    !integer(value.issuedAt) ||
    !integer(value.expiresAt) ||
    !hosts(value.hosts) ||
    value.expiresAt <= value.issuedAt ||
    value.expiresAt - value.issuedAt > publicationLimits.selectorLifetimeMs ||
    !(
      value.active === null ||
      (object(value.active) &&
        identifier(value.active.id) &&
        identifier(value.active.build) &&
        integer(value.active.safetyVersion) &&
        value.active.safetyVersion === value.safetyVersion)
    )
  )
    return null;
  return value as PublicationSelector;
}

/**
 * `partial` reads a candidate's saved progress: every entry is checked, but its routes need not
 * all be prepared yet. Delivery and activation always read complete manifests.
 */
export function parsePublicationManifest(
  text: string,
  { partial = false } = {},
): PublicationManifest | null {
  const value = decode(text, publicationLimits.manifestBytes);
  if (
    !object(value) ||
    value.format !== 1 ||
    (value.reuseVersion !== undefined && value.reuseVersion !== 1) ||
    (value.verification !== undefined &&
      (!object(value.verification) ||
        value.verification.version !== 1 ||
        (value.verification.parent !== undefined && !identifier(value.verification.parent)))) ||
    !(value.presentation === undefined || value.presentation === 2) ||
    !identifier(value.id) ||
    !identifier(value.creatorId) ||
    !identifier(value.build) ||
    !integer(value.sourceVersion) ||
    !integer(value.safetyVersion) ||
    !integer(value.createdAt) ||
    !hosts(value.hosts) ||
    (value.publicAccess !== undefined &&
      (typeof value.publicAccess !== "string" || !hashPattern.test(value.publicAccess))) ||
    !Array.isArray(value.routes) ||
    value.routes.length > publicationLimits.routes ||
    !Array.isArray(value.representations) ||
    value.representations.length > publicationLimits.representations ||
    !Array.isArray(value.fallbacks) ||
    value.fallbacks.length > publicationLimits.routes
  )
    return null;
  if (
    value.publicFunctions !== undefined &&
    (!Array.isArray(value.publicFunctions) ||
      value.publicFunctions.length > 32 ||
      value.publicFunctions.some(
        (path) => typeof path !== "string" || !/^\/_serverFn\/[A-Za-z0-9_.-]{1,512}$/.test(path),
      ))
  )
    return null;
  if (
    value.assets !== undefined &&
    (!Array.isArray(value.assets) ||
      value.assets.length > publicationLimits.assets ||
      value.assets.some(
        (asset) =>
          !object(asset) ||
          typeof asset.source !== "string" ||
          asset.source.length > 1000 ||
          !/^\/api\/(?:media|shop\/assets)\/[a-zA-Z0-9-]+(?:\?w=\d+)?$/.test(asset.source) ||
          (asset.sourceVersion !== undefined &&
            (typeof asset.sourceVersion !== "string" || !hashPattern.test(asset.sourceVersion))) ||
          typeof asset.hash !== "string" ||
          !hashPattern.test(asset.hash) ||
          !integer(asset.bytes) ||
          asset.bytes < 1 ||
          asset.bytes > publicationLimits.assetBytes ||
          typeof asset.type !== "string" ||
          !publicationAssetType.test(asset.type),
      ))
  )
    return null;
  if (
    value.routes.some(
      (route) =>
        !object(route) ||
        typeof route.path !== "string" ||
        !publishedPresentationPath(route.path) ||
        route.path.length > 250 ||
        /[?#]/.test(route.path) ||
        !["public", "viewer"].includes(String(route.policy)) ||
        publishedRoutePolicy(route.path, route.policy === "viewer") !== route.policy ||
        !Array.isArray(route.dependencies) ||
        !route.dependencies.every((key) => dependencies.has(key)),
    )
  )
    return null;
  const routes = new Map((value.routes as PublicationRoute[]).map((route) => [route.path, route]));
  if (routes.size !== value.routes.length) return null;
  if (
    value.enhancedPaths !== undefined &&
    (value.presentation !== 2 ||
      !Array.isArray(value.enhancedPaths) ||
      value.enhancedPaths.length > routes.size ||
      value.enhancedPaths.some(
        (path) => typeof path !== "string" || routes.get(path)?.policy !== "viewer",
      ))
  )
    return null;
  const identities = new Set<string>();
  const assetHashes = new Set(
    (value.assets as PublicationAsset[] | undefined)?.map((asset) => asset.hash),
  );
  for (const representation of value.representations) {
    if (
      !object(representation) ||
      typeof representation.origin !== "string" ||
      !value.hosts.some((host: PublicationHost) => host.origin === representation.origin) ||
      typeof representation.request !== "string" ||
      !representation.request.startsWith("/") ||
      representation.request.startsWith("//") ||
      representation.request.length > 9000 ||
      typeof representation.route !== "string" ||
      !routes.has(representation.route) ||
      representation.policy !== routes.get(representation.route)!.policy ||
      typeof representation.hash !== "string" ||
      !hashPattern.test(representation.hash) ||
      (representation.inputHash !== undefined &&
        (typeof representation.inputHash !== "string" ||
          !hashPattern.test(representation.inputHash))) ||
      !integer(representation.bytes) ||
      !representation.bytes ||
      representation.bytes > publicationLimits.bodyBytes ||
      ![200, 301, 302, 307, 308].includes(Number(representation.status)) ||
      !Array.isArray(representation.headers) ||
      (representation.assets !== undefined &&
        (!Array.isArray(representation.assets) ||
          representation.assets.length > publicationLimits.assets ||
          representation.assets.some((hash) => !assetHashes.has(hash)))) ||
      representation.headers.length > 10 ||
      representation.headers.some(
        (pair) =>
          !Array.isArray(pair) ||
          pair.length !== 2 ||
          !publicationStoredHeaders.has(pair[0]) ||
          typeof pair[1] !== "string" ||
          /[\r\n]/.test(pair[1]),
      )
    )
      return null;
    const headers = new Headers(representation.headers as [string, string][]);
    const type = headers.get("content-type")?.split(";")[0];
    const data = representation.request.startsWith("/_serverFn/");
    if (representation.status === 200 && type !== (data ? "application/json" : "text/html"))
      return null;
    const location = headers.get("location");
    if (
      representation.status !== 200 &&
      (!location || !location.startsWith("/") || location.startsWith("//"))
    )
      return null;
    const identity = representation.origin + representation.request;
    if (identities.has(identity)) return null;
    identities.add(identity);
  }
  if (
    value.fallbacks.some(
      (fallback) =>
        !object(fallback) ||
        typeof fallback.path !== "string" ||
        !fallback.path.startsWith("/") ||
        typeof fallback.reason !== "string" ||
        fallback.reason.length > 200,
    )
  )
    return null;
  if (partial) return value as PublicationManifest;
  // Both initial HTML and navigation data must exist on every advertised host.
  for (const host of value.hosts)
    for (const route of routes.values())
      if (
        !value.representations.some(
          (entry: PublicationRepresentation) =>
            entry.origin === host.origin && entry.request === route.path,
        ) ||
        !value.representations.some(
          (entry: PublicationRepresentation) =>
            entry.origin === host.origin &&
            entry.route === route.path &&
            entry.request.startsWith("/_serverFn/"),
        )
      )
        return null;
  return value as PublicationManifest;
}

export function publishedAssetPath(hash: string) {
  return `/_published/media/${hash}`;
}

export function publishedAssetHash(path: string) {
  return /^\/_published\/media\/([a-f0-9]{64})$/.exec(path)?.[1] ?? null;
}

export function publicationSelectorKey(creatorId: string) {
  if (!identifier(creatorId)) throw new Error("Invalid creator");
  return `published-sites/v1/selectors/${creatorId}.json`;
}

export function publicationManifestKey(creatorId: string, id: string) {
  if (!identifier(creatorId) || !identifier(id)) throw new Error("Invalid publication");
  return `published-sites/v1/manifests/${creatorId}/${id}.json`;
}

export function publicationObjectKey(creatorId: string, hash: string) {
  if (!identifier(creatorId) || !hashPattern.test(hash))
    throw new Error("Invalid publication object");
  return `published-sites/v1/objects/${creatorId}/${hash}`;
}

export async function publicationHash(bytes: BufferSource) {
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function publicationNeedsRender(dirty: readonly string[], route: PublicationRoute) {
  return (
    dirty.includes("all") ||
    dirty.includes("chrome") ||
    route.dependencies.some((dependency) => dirty.includes(dependency))
  );
}

export function publicationRetryAt(attempt: number, now = Date.now()) {
  return now + Math.min(15 * 60_000, 5000 * 2 ** Math.min(8, Math.max(0, attempt)));
}
